
import React, { useState } from 'react';
import {
  Search,
  FormInput,
  Type,
  ArrowUpDown,
  Eye,
  EyeOff,
  CheckCircle,
  Move,
  Loader2
} from 'lucide-react';
import { formFieldsData } from '../../../types/settings/FormFieldProps';

interface ModernFormFieldTableProps {
  initialData: formFieldsData[];
  onEdit?: (field: formFieldsData) => void;
  onSearch?: (term: string) => Promise<void>;
  onSort?: (column: string, order: 'ASC' | 'DESC') => Promise<void>;
  isLoading?: boolean;
  searchTerm?: string;
  sortColumn?: string;
  sortOrder?: 'ASC' | 'DESC';
  onSaveColumnOrder?: (orderData: { columns: { uuid: string; col_order: number }[] }) => Promise<void>;
}

const ModernFormFieldTable: React.FC<ModernFormFieldTableProps> = ({
  initialData,
  onEdit,
  onSearch,
  onSort,
  isLoading = false,
  searchTerm = '',
  sortColumn,
  sortOrder
}) => {
  const [searchInput] = useState(searchTerm);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      await onSearch(searchInput);
    }
  };

  const handleSort = (column: string) => {
    if (onSort) {
      const newOrder = column === sortColumn && sortOrder === 'ASC' ? 'DESC' : 'ASC';
      onSort(column, newOrder);
    }
  };

  const getTypeIcon = (type: string | undefined) => {
    switch (type?.toLowerCase()) {
      case 'text':
      case 'varchar':
        return <Type className="w-4 h-4" />;
      case 'email':
        return <FormInput className="w-4 h-4" />;
      case 'phone':
        return <FormInput className="w-4 h-4" />;
      case 'select':
      case 'dropdown':
        return <FormInput className="w-4 h-4" />;
      default:
        return <FormInput className="w-4 h-4" />;
    }
  };

  const StatusBadge = ({ active, required }: { active: boolean; required: boolean }) => (
    <div className="flex gap-1">
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        active 
          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
      }`}>
        {active ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
        {active ? 'Visible' : 'Hidden'}
      </span>
      {required && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
          <CheckCircle className="w-3 h-3 mr-1" />
          Required
        </span>
      )}
    </div>
  );

  const getSortIcon = (column: string) => {
    if (column !== sortColumn) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return <ArrowUpDown className={`w-4 h-4 ${sortOrder === 'ASC' ? 'text-brandColor' : 'text-brandColor rotate-180'}`} />;
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brandColor" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading form fields...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <form onSubmit={handleSearch} className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search form fields..."
              value={searchInput}
              onChange={(e) => onSearch?.(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brandColor transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('col_order')}
                    className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <Move className="w-4 h-4" />
                    <span>Order</span>
                    {getSortIcon('col_order')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('col_label')}
                    className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <FormInput className="w-4 h-4" />
                    <span>Field Label</span>
                    {getSortIcon('col_label')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('col_type')}
                    className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <Type className="w-4 h-4" />
                    <span>Field Type</span>
                    {getSortIcon('col_type')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <span>Status</span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('col_name')}
                    className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <span>Database Name</span>
                    {getSortIcon('col_name')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {initialData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FormInput className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">No form fields found</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                        {searchInput ? 'Try adjusting your search criteria' : 'Create your first form field to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                initialData.map((field) => (
                  <tr 
                    key={field.uuid} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                    onClick={() => onEdit?.(field)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900/20 text-brandColor dark:text-blue-400 rounded-full text-sm font-medium">
                          {field.col_order || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-3">
                          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                            {getTypeIcon(field.col_type)}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {field.col_label || 'Untitled Field'}
                          </div>
                          {(field as any).col_placeholder && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {(field as any).col_placeholder}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900 dark:text-white font-medium">
                          {field.col_type ? field.col_type.charAt(0).toUpperCase() + field.col_type.slice(1) : 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge
                        active={(field as any).col_visible !== false}
                        required={field.col_required === true}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {field.col_name || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ModernFormFieldTable;